/**
 * Admin V3 — Drivers — Documents Panel (Plan §2.2 M-F-6).
 * Lists driver_documents via GET /admin/drivers/{id}/documents.
 * Mount DocumentUploadModal + DocumentVerificationModal for admin actions.
 *
 * G11.C — Sort + filter controls (by document type, by verification status,
 * by sort order). Pure client-side over the already-fetched list, no
 * additional backend round-trip.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { FileText, Upload, Check, X as XIcon, RotateCcw } from 'lucide-react';
import { SectionCard, EmptyState, verificationStatusMeta } from '../../shared/visualTokens';
import { Button } from '../../../components/ui/button';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import { t } from '../../i18n';
import DocumentUploadModal from './DocumentUploadModal';
import DocumentVerificationModal from './DocumentVerificationModal';
import DocumentViewer from './DocumentViewer';

// G11.C — Filter vocabularies. Doc types match Plan §2.3.B exactly.
const DOC_TYPES = [
  'permis_de_conduire',
  'carte_grise',
  'attestation_assurance',
  'capacite_professionnelle',
  'kbis',
  'casier_judiciaire',
  'justificatif_domicile',
  'identity_document',
];
const STATUSES = ['pending', 'verified', 'rejected', 'renewal_requested', 'expired'];

// RBAC HIDE gating (P0-1a) — permission strings mirror the backend
// @rbac_permission on the document routes (admin_driver_compliance.py). The
// upload POST /admin/drivers/{id}/documents is gated by DOCUMENT_VIEW; the
// verify/reject/request_renewal row actions carry their own permissions.
// Fail-closed HIDE via usePermissions().can(); backend remains authoritative.
const PERM_DOC_VIEW = 'admin:driver:document:view';
const PERM_DOC_VERIFY = 'admin:driver:document:verify';
const PERM_DOC_REJECT = 'admin:driver:document:reject';
const PERM_DOC_RENEWAL = 'admin:driver:document:request_renewal';

const DriverDocumentsPanel = ({ driverId, locale = 'fr' }) => {
  const { data, loading, error, refetch } = useAdminQuery(
    driverId ? `/admin/drivers/${driverId}/documents` : null,
    {},
  );
  const docs = Array.isArray(data) ? data : [];
  const { can } = usePermissions();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState(null);     // { docId, action: 'verify'|'reject'|'request_renewal' }
  const [viewerTarget, setViewerTarget] = useState(null);

  // G11.C — sort + filter state.
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortMode, setSortMode] = useState('uploaded_desc'); // | 'uploaded_asc' | 'type_asc' | 'expiry_asc'

  const visibleDocs = useMemo(() => {
    let out = docs.slice();
    if (typeFilter !== 'all')   out = out.filter((d) => d.document_type === typeFilter);
    if (statusFilter !== 'all') out = out.filter((d) => d.verification_status === statusFilter);
    out.sort((a, b) => {
      switch (sortMode) {
        case 'uploaded_asc':
          return (a.uploaded_at || '').localeCompare(b.uploaded_at || '');
        case 'type_asc':
          return (a.document_type || '').localeCompare(b.document_type || '');
        case 'expiry_asc':
          // Documents without expiry_date sort last.
          if (!a.expiry_date && !b.expiry_date) return 0;
          if (!a.expiry_date) return 1;
          if (!b.expiry_date) return -1;
          return a.expiry_date.localeCompare(b.expiry_date);
        case 'uploaded_desc':
        default:
          return (b.uploaded_at || '').localeCompare(a.uploaded_at || '');
      }
    });
    return out;
  }, [docs, typeFilter, statusFilter, sortMode]);

  const handleUploaded = useCallback(() => {
    setUploadOpen(false);
    refetch();
  }, [refetch]);

  const handleVerified = useCallback(() => {
    setVerifyTarget(null);
    refetch();
  }, [refetch]);

  return (
    <SectionCard
      icon={FileText}
      iconClass="bg-emerald-100 text-emerald-700"
      title={t('compliance.documents.title', locale)}
      action={
        can(PERM_DOC_VIEW) ? (
          <Button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="inline-flex items-center gap-2"
            data-testid="driver-documents-upload-btn"
          >
            <Upload className="h-4 w-4" />
            {t('compliance.documents.action.upload', locale)}
          </Button>
        ) : null
      }
    >
      {loading && <p className="text-slate-500 text-sm">{t('compliance.common.loading', locale)}</p>}
      {error && <p className="text-rose-700 text-sm">{t('compliance.common.error', locale)}</p>}

      {/* G11.C — Filter + sort toolbar (pure client-side). */}
      {!loading && !error && docs.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 pb-3 text-xs text-slate-600" data-testid="documents-toolbar">
          <label className="flex items-center gap-1">
            <span>{t('compliance.queue.filter.doctype', locale)}</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border border-slate-200 rounded px-2 py-1 bg-white"
              data-testid="documents-filter-doctype"
            >
              <option value="all">{t('compliance.queue.filter.alltypes', locale)}</option>
              {DOC_TYPES.map((tk) => (
                <option key={tk} value={tk}>{t(`compliance.doctype.${tk}`, locale)}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1">
            <span>{t('compliance.filter.status', locale)}</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-200 rounded px-2 py-1 bg-white"
              data-testid="documents-filter-status"
            >
              <option value="all">{t('compliance.filter.allstatuses', locale)}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{t(`compliance.filter.${s}`, locale)}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1">
            <span>{t('compliance.sort.label', locale)}</span>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              className="border border-slate-200 rounded px-2 py-1 bg-white"
              data-testid="documents-sort"
            >
              <option value="uploaded_desc">{t('compliance.sort.uploaded_desc', locale)}</option>
              <option value="uploaded_asc">{t('compliance.sort.uploaded_asc', locale)}</option>
              <option value="type_asc">{t('compliance.sort.type_asc', locale)}</option>
              <option value="expiry_asc">{t('compliance.sort.expiry_asc', locale)}</option>
            </select>
          </label>
        </div>
      )}

      {!loading && !error && docs.length === 0 && (
        <EmptyState
          icon={FileText}
          title={t('compliance.documents.empty.title', locale)}
          desc={t('compliance.documents.empty.desc', locale)}
        />
      )}

      {!loading && !error && docs.length > 0 && (
        <div className="overflow-x-auto" data-testid="driver-documents-table">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-600">
                <th className="py-2 pr-4">{t('compliance.documents.table.type', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.documents.table.version', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.documents.table.status', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.documents.table.expiry', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.documents.table.uploaded_at', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.documents.table.actions', locale)}</th>
              </tr>
            </thead>
            <tbody>
              {visibleDocs.map((d) => {
                const meta = verificationStatusMeta[d.verification_status];
                return (
                  <tr key={d.id} className="border-b border-slate-100">
                    <td className="py-2 pr-4 text-slate-900">{t(`compliance.doctype.${d.document_type}`, locale)}</td>
                    <td className="py-2 pr-4 text-slate-700">v{d.version}</td>
                    <td className="py-2 pr-4">
                      {meta ? (
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${meta.pill}`}>
                          {t(meta.labelKey, locale)}
                        </span>
                      ) : (
                        <span className="text-slate-600">{d.verification_status}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-slate-700">{d.expiry_date || '—'}</td>
                    <td className="py-2 pr-4 text-slate-700">{d.uploaded_at || '—'}</td>
                    <td className="py-2 pr-4 space-x-2 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setViewerTarget(d)}
                        className="text-blue-700 hover:underline text-xs"
                        data-testid={`doc-view-${d.id}`}
                      >
                        {t('compliance.documents.action.view', locale)}
                      </button>
                      {d.verification_status === 'pending' && (
                        <>
                          {can(PERM_DOC_VERIFY) && (
                            <button
                              type="button"
                              onClick={() => setVerifyTarget({ docId: d.id, action: 'verify' })}
                              className="text-emerald-700 hover:underline text-xs inline-flex items-center gap-1"
                              data-testid={`doc-verify-${d.id}`}
                            >
                              <Check className="h-3 w-3" />{t('compliance.documents.action.verify', locale)}
                            </button>
                          )}
                          {can(PERM_DOC_REJECT) && (
                            <button
                              type="button"
                              onClick={() => setVerifyTarget({ docId: d.id, action: 'reject' })}
                              className="text-rose-700 hover:underline text-xs inline-flex items-center gap-1"
                              data-testid={`doc-reject-${d.id}`}
                            >
                              <XIcon className="h-3 w-3" />{t('compliance.documents.action.reject', locale)}
                            </button>
                          )}
                        </>
                      )}
                      {d.verification_status === 'verified' && can(PERM_DOC_RENEWAL) && (
                        <button
                          type="button"
                          onClick={() => setVerifyTarget({ docId: d.id, action: 'request_renewal' })}
                          className="text-amber-700 hover:underline text-xs inline-flex items-center gap-1"
                          data-testid={`doc-renew-${d.id}`}
                        >
                          <RotateCcw className="h-3 w-3" />{t('compliance.documents.action.request_renewal', locale)}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {uploadOpen && (
        <DocumentUploadModal
          driverId={driverId}
          onClose={() => setUploadOpen(false)}
          onSuccess={handleUploaded}
          locale={locale}
        />
      )}
      {verifyTarget && (
        <DocumentVerificationModal
          driverId={driverId}
          documentId={verifyTarget.docId}
          action={verifyTarget.action}
          onClose={() => setVerifyTarget(null)}
          onSuccess={handleVerified}
          locale={locale}
        />
      )}
      {viewerTarget && (
        <DocumentViewer
          driverId={driverId}
          document={viewerTarget}
          onClose={() => setViewerTarget(null)}
          locale={locale}
        />
      )}
    </SectionCard>
  );
};

export default DriverDocumentsPanel;
